import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Activity, ArrowRight, Mail, Globe, TrendingUp,
  CreditCard, ShoppingBag, Sparkles, AlertCircle, Loader2, ExternalLink,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appMetrics, setAppMetrics] = useState(null);
  const [appLoading, setAppLoading] = useState(true);
  const [appError, setAppError] = useState(null);

  useEffect(() => {
    adminApi.stats().then(setStats).catch(() => setStats(null)).finally(() => setLoading(false));
    adminApi.appBridgeMetrics()
      .then((m) => setAppMetrics(m))
      .catch((e) => setAppError(e?.message || String(e)))
      .finally(() => setAppLoading(false));
  }, []);

  const statusTiles = [
    { key: "lead", label: "Leads", color: "text-[color:var(--gv-pink)]" },
    { key: "contacted", label: "Contacted", color: "text-[color:var(--gv-blue)]" },
    { key: "qualified", label: "Qualified", color: "text-emerald-600" },
    { key: "customer", label: "Customers", color: "text-amber-600" },
    { key: "lost", label: "Lost", color: "text-rose-500" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)] mb-2">Dashboard</h1>
      <p className="text-sm text-[color:var(--gv-ink)]/60 mb-8">Snapshot unifié — geovinum.io CRM · app.geovinum.io users.</p>

      {/* Section 1 — geovinum.io CRM */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <Globe size={16} className="text-[color:var(--gv-ink)]/60" />
          <h2 className="font-display text-lg text-[color:var(--gv-ink)]">geovinum.io — CRM</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
          <KpiCard icon={Users} label="Contacts total" value={loading ? "—" : stats?.contacts_total ?? 0} link="/admin/crm" />
          <KpiCard icon={Activity} label="Actions logged" value={loading ? "—" : stats?.actions_total ?? 0} link="/admin/crm" />
        </div>
        <h3 className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-3">Pipeline status</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {statusTiles.map((s) => (
            <div
              key={s.key}
              className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5"
              data-testid={`admin-tile-${s.key}`}
            >
              <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mb-1.5">{s.label}</div>
              <div className={`font-display text-3xl ${s.color}`}>
                {loading ? "—" : stats?.by_status?.[s.key] ?? 0}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 2 — app.geovinum.io (via bridge) */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={16} className="text-[color:var(--gv-pink)]" />
          <h2 className="font-display text-lg text-[color:var(--gv-ink)]">app.geovinum.io — Users &amp; Revenue</h2>
          {appMetrics?.computed_at && (
            <span className="text-[11px] text-[color:var(--gv-ink)]/45 ml-2">
              màj {new Date(appMetrics.computed_at).toLocaleTimeString("fr-FR")}
            </span>
          )}
        </div>

        {appLoading ? (
          <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6 text-sm text-[color:var(--gv-ink)]/55 flex items-center gap-2" data-testid="app-metrics-loading">
            <Loader2 size={14} className="animate-spin" /> Récupération des KPIs app.geovinum.io…
          </div>
        ) : appError ? (
          <div className="bg-rose-50 border border-rose-200 rounded-[5px] p-4 text-sm text-rose-700 flex items-start gap-2" data-testid="app-metrics-error">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-medium mb-0.5">Impossible de contacter app.geovinum.io</div>
              <div className="text-[12px] opacity-80">{appError}</div>
            </div>
          </div>
        ) : appMetrics ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="app-metrics-grid">
            <AppKpi
              icon={Users}
              label="Utilisateurs"
              value={appMetrics.users?.total ?? 0}
              hint={`+${appMetrics.users?.new_month ?? 0} ce mois · +${appMetrics.users?.new_week ?? 0} cette semaine`}
              testid="app-kpi-users"
            />
            <AppKpi
              icon={CreditCard}
              label="Abonnés actifs"
              value={
                (appMetrics.subscriptions?.amateur_active ?? 0) +
                (appMetrics.subscriptions?.pro_active ?? 0)
              }
              hint={`${appMetrics.subscriptions?.amateur_active ?? 0} Amateur · ${appMetrics.subscriptions?.pro_active ?? 0} Pro`}
              testid="app-kpi-subs"
            />
            <AppKpi
              icon={TrendingUp}
              label="MRR"
              value={`${(appMetrics.subscriptions?.mrr_eur ?? 0).toFixed(2)} €`}
              hint={`Conversion ${(appMetrics.subscriptions?.conversion_pct ?? 0).toFixed(1)}%`}
              testid="app-kpi-mrr"
            />
            <AppKpi
              icon={ShoppingBag}
              label="Boutique — 30j"
              value={`${(appMetrics.shop?.last_30d_eur ?? 0).toFixed(2)} €`}
              hint={`${appMetrics.shop?.orders_paid_30d ?? 0} commandes payées / ${appMetrics.shop?.orders_pending ?? 0} en attente`}
              testid="app-kpi-shop-30d"
            />
            <AppKpi
              icon={Activity}
              label="Activité"
              value={`${appMetrics.activity?.dau ?? 0} DAU`}
              hint={`${appMetrics.activity?.mau ?? 0} MAU · IA upsell ${appMetrics.ai_upsell?.paywall_triggers_30d ?? 0}/30j`}
              testid="app-kpi-activity"
            />
            <AppKpi
              icon={AlertCircle}
              label="Modération"
              value={appMetrics.moderation?.pending_claims ?? 0}
              hint="Revendications en attente"
              testid="app-kpi-claims"
              highlight={(appMetrics.moderation?.pending_claims ?? 0) > 0}
            />
          </div>
        ) : null}
      </div>

      {/* Quick actions */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickLink to="/admin/crm" label="Manage CRM" />
        <QuickLink to="/admin/crm?import=1" label="Import CSV" />
        <QuickLink href="mailto:hello@geovinum.io" label="Email support" icon={Mail} />
      </div>
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, link }) => (
  <Link
    to={link || "#"}
    className="block bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6 hover:border-[color:var(--gv-pink)] transition-colors"
  >
    <div className="flex items-center justify-between mb-3">
      <Icon size={20} className="text-[color:var(--gv-pink)]" />
      <ArrowRight size={14} className="text-[color:var(--gv-ink)]/40" />
    </div>
    <div className="font-display text-4xl text-[color:var(--gv-ink)] mb-1">{value}</div>
    <div className="text-[12.5px] text-[color:var(--gv-ink)]/60 uppercase tracking-widest">{label}</div>
  </Link>
);

const AppKpi = ({ icon: Icon, label, value, hint, testid, highlight = false }) => (
  <div
    className={`bg-white border rounded-[5px] p-5 ${
      highlight
        ? "border-[color:var(--gv-pink)]"
        : "border-[color:var(--gv-line)]"
    }`}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-2">
      <Icon size={14} className={highlight ? "text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]/50"} />
      <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60">{label}</div>
    </div>
    <div className="font-display text-3xl text-[color:var(--gv-ink)] mb-1">{value}</div>
    {hint && <div className="text-[11.5px] text-[color:var(--gv-ink)]/55">{hint}</div>}
  </div>
);

const QuickLink = ({ to, href, label, icon: Icon }) => {
  const cls =
    "bg-white border border-[color:var(--gv-line)] rounded-[5px] px-5 py-4 text-sm flex items-center justify-between hover:border-[color:var(--gv-pink)] transition-colors";
  if (href)
    return (
      <a href={href} className={cls}>
        {Icon && <Icon size={14} />}
        <span>{label}</span>
        <ExternalLink size={14} />
      </a>
    );
  return (
    <Link to={to} className={cls}>
      <span>{label}</span>
      <ArrowRight size={14} />
    </Link>
  );
};

export default AdminDashboardPage;
